import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Terminal, Database, BookOpenIcon, PenToolIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { getQuestionCount } from "@/services/aiQuestionService";
import { DifficultyLevel } from "@/services/questionGeneratorService";
import { toast } from "sonner";
import { createFigureQuestionPrompt } from "@/lib/htmlUtils";

interface Grade {
  id: string;
  name: string;
}

interface Subject {
  id: string;
  name: string;
}

interface Category {
  id: string;
  name: string;
}

interface QuestionGeneratorFormProps {
  onGenerateQuestions: (params: {
    grade: string;
    subjectId: string;
    categoryId: string;
    count: number;
    difficulty: DifficultyLevel;
    customPrompt?: string;
    generateWithTopic?: boolean;
  }) => Promise<void>;
  isGenerating: boolean;
}

const QuestionGeneratorForm: React.FC<QuestionGeneratorFormProps> = ({
  onGenerateQuestions,
  isGenerating
}) => {
  const [selectedGrade, setSelectedGrade] = useState<string>("");
  const [selectedSubject, setSelectedSubject] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [questionCount, setQuestionCount] = useState<number>(10);
  const [difficulty, setDifficulty] = useState<DifficultyLevel>("medium");
  const [showPrompt, setShowPrompt] = useState<boolean>(false);
  const [aiPrompt, setAiPrompt] = useState<string>("");
  const [defaultPrompt, setDefaultPrompt] = useState<string>("");
  const [grades, setGrades] = useState<Grade[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [existingQuestionCount, setExistingQuestionCount] = useState<number>(0);
  const [isLoadingCount, setIsLoadingCount] = useState<boolean>(false);
  const [promptType, setPromptType] = useState<"standard" | "topic" | "figure">("standard");

  useEffect(() => {
    const fetchGrades = async () => {
      try {
        const { data, error } = await supabase
          .from("subjects")
          .select("grade")
          .order("grade");
        
        if (error) {
          console.error("Error fetching grades:", error);
          return;
        }

        const uniqueGrades = [...new Set(data.map(item => item.grade))];
        const formattedGrades = uniqueGrades.map(grade => ({
          id: grade,
          name: grade
        }));
        
        setGrades(formattedGrades);
      } catch (err) {
        console.error("Error in fetchGrades:", err);
      }
    };

    fetchGrades();
  }, []);

  useEffect(() => {
    const fetchSubjects = async () => {
      if (!selectedGrade) {
        setSubjects([]);
        return;
      }
      
      const { data, error } = await supabase
        .from("subjects")
        .select("*")
        .eq("grade", selectedGrade)
        .order("name");
      
      if (error) {
        console.error("Error fetching subjects:", error);
        return;
      }
      
      setSubjects(data);
    };

    fetchSubjects();
  }, [selectedGrade]);

  useEffect(() => {
    const fetchCategories = async () => {
      if (!selectedSubject) {
        setCategories([]);
        return;
      }
      
      const { data, error } = await supabase
        .from("subjects_categories")
        .select("*")
        .eq("subject_id", selectedSubject)
        .order("name");
      
      if (error) {
        console.error("Error fetching categories:", error);
        return;
      }
      
      setCategories(data);
    };

    fetchCategories();
  }, [selectedSubject]);

  useEffect(() => {
    const fetchQuestionCount = async () => {
      if (!selectedSubject) {
        setExistingQuestionCount(0);
        return;
      }
      
      setIsLoadingCount(true);
      
      try {
        const count = await getQuestionCount(selectedSubject, selectedCategory);
        setExistingQuestionCount(count);
      } catch (error) {
        console.error("Error fetching question count:", error);
      } finally {
        setIsLoadingCount(false);
      }
    };
    
    fetchQuestionCount();
  }, [selectedSubject, selectedCategory]);

  useEffect(() => {
    if (selectedGrade && selectedSubject) {
      const subject = subjects.find(s => s.id === selectedSubject)?.name || "";
      const category = categories.find(c => c.id === selectedCategory)?.name || "";
      
      const basePrompt = `Generate ${questionCount} multiple-choice questions for ${selectedGrade} grade ${subject}${category ? ` in the category of ${category}` : ''} with ${difficulty} difficulty level.`;

      const topicPrompt = `
You are an educational content expert. First, select an appropriate topic for ${selectedGrade} grade ${subject}${category ? ` in the category of ${category}` : ''} and generate comprehensive content for it. Then, generate ${questionCount} multiple-choice questions based on this topic content.

1. First, create a topic with:
   - An appropriate title for the selected topic
   - Detailed educational content about the topic (can include examples, definitions, explanations)
   - The content should be suitable for ${selectedGrade} grade level
   - Use appropriate HTML formatting for better presentation

2. Then, generate ${questionCount} multiple-choice questions that:
   - Test different aspects of the topic content you created
   - Are appropriate for ${difficulty} difficulty level
   - Include clear explanations for the correct answers
   - Use HTML formatting where appropriate

You may use HTML formatting in content, questions, options, and explanations where appropriate, such as:
- <sup> and <sub> for superscript/subscript (e.g., H<sub>2</sub>O, 10<sup>2</sup>)
- <i> for italics
- <b> for bold text
- <br> for line breaks
- Mathematical symbols and equations (use appropriate HTML)
- Tables using <table>, <tr>, <td> tags if needed
- Lists using <ul>, <ol>, <li> tags if appropriate

Format the response as a JSON object with:
{
  "topic": {
    "title": "The selected topic title",
    "content": "The detailed topic content with HTML formatting"
  },
  "questions": [
    {
      "text": "Question text (may include HTML)",
      "options": [
        {
          "text": "Option text (may include HTML)",
          "isCorrect": boolean
        }
      ],
      "explanation": "Explanation for correct answer (may include HTML)",
      "difficulty": "${difficulty}"
    }
  ]
}

Make sure all content is:
- Age-appropriate for ${selectedGrade} grade
- Factually accurate
- Directly relevant to the subject and category
- Properly formatted with HTML where needed
- Matches ${difficulty} difficulty level`;

      const standardPrompt = `
${basePrompt}

For each question:
1. Provide a clear question text that is appropriate for ${difficulty} difficulty (easier/simpler for "easy", more challenging concepts for "hard")
2. Give four possible answers (options)
3. Mark exactly one option as correct
4. Provide a brief explanation for the correct answer
5. Add a "difficulty" field with value "${difficulty}"

You may use HTML formatting in questions, options, and explanations where appropriate, such as:
- <sup> and <sub> for superscript/subscript (e.g., H<sub>2</sub>O, 10<sup>2</sup>)
- <i> for italics
- <b> for bold text
- <br> for line breaks
- Mathematical symbols and equations (use appropriate HTML)
- Tables using <table>, <tr>, <td> tags if needed
- Lists using <ul>, <ol>, <li> tags if appropriate

Format the response as a JSON array of questions, where each question has:
- text: the question text (may include HTML)
- options: an array of option objects, each with:
  - text: the option text (may include HTML)
  - isCorrect: boolean indicating if this is the correct answer
- explanation: brief explanation of the correct answer (may include HTML)
- difficulty: "${difficulty}"

Make sure the questions are age-appropriate, factually accurate, and directly relevant to the subject and category.
Ensure the difficulty level matches ${difficulty} (${
        difficulty === "easy" ? "simpler concepts and more straightforward questions" : 
        difficulty === "hard" ? "complex concepts and challenging questions" : 
        "balanced difficulty - not too easy, not too hard"
      }).`;

      // Get figure-based prompt from htmlUtils
      const figurePrompt = createFigureQuestionPrompt(
        questionCount,
        subject,
        selectedGrade,
        category,
        difficulty
      );
      
      // Set default prompt based on currently selected prompt type
      let currentPrompt = standardPrompt;
      switch(promptType) {
        case "topic": 
          currentPrompt = topicPrompt;
          break;
        case "figure":
          currentPrompt = figurePrompt;
          break;
        default:
          currentPrompt = standardPrompt;
      }
      
      setDefaultPrompt(currentPrompt);
      setAiPrompt(currentPrompt);
    }
  }, [selectedGrade, selectedSubject, selectedCategory, questionCount, difficulty, subjects, categories, promptType]);

  const handleResetPrompt = () => {
    setAiPrompt(defaultPrompt);
    toast.info("Prompt reset to default");
  };

  const handleSubmit = async () => {
    if (!selectedGrade || !selectedSubject || !selectedCategory) {
      toast.error("Error", {
        description: "Please select grade, subject, and category",
      });
      return;
    }

    const params: any = {
      grade: selectedGrade,
      subjectId: selectedSubject,
      categoryId: selectedCategory,
      count: questionCount,
      difficulty,
      generateWithTopic: promptType === "topic"
    };

    if (showPrompt) {
      params.customPrompt = aiPrompt;
    }

    console.log(`Generating questions with params:`, params);
    await onGenerateQuestions(params);
  };

  useEffect(() => {
    setSelectedSubject("");
    setSelectedCategory("");
  }, [selectedGrade]);

  useEffect(() => {
    setSelectedCategory("");
  }, [selectedSubject]);

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div>
          <label htmlFor="grade-select" className="block text-sm font-medium mb-1">
            Grade
          </label>
          <Select 
            value={selectedGrade} 
            onValueChange={setSelectedGrade}
          >
            <SelectTrigger id="grade-select">
              <SelectValue placeholder="Select grade" />
            </SelectTrigger>
            <SelectContent>
              {grades?.map((grade) => (
                <SelectItem key={grade.id} value={grade.id}>
                  {grade.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <label htmlFor="subject-select" className="block text-sm font-medium mb-1">
            Subject
          </label>
          <Select 
            value={selectedSubject} 
            onValueChange={setSelectedSubject}
            disabled={!selectedGrade}
          >
            <SelectTrigger id="subject-select">
              <SelectValue placeholder="Select subject" />
            </SelectTrigger>
            <SelectContent>
              {subjects?.map((subject) => (
                <SelectItem key={subject.id} value={subject.id}>
                  {subject.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <label htmlFor="category-select" className="block text-sm font-medium mb-1">
            Category
          </label>
          <Select 
            value={selectedCategory} 
            onValueChange={setSelectedCategory}
            disabled={!selectedSubject}
          >
            <SelectTrigger id="category-select">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories?.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <label htmlFor="question-count" className="block text-sm font-medium mb-1">
            Number of Questions
          </label>
          <Input
            id="question-count"
            type="number"
            min="1"
            max="50"
            value={questionCount}
            onChange={(e) => setQuestionCount(parseInt(e.target.value) || 10)}
            className="w-full"
          />
        </div>
      </div>
      
      <div className="mb-4">
        <Label className="block text-sm font-medium mb-2">Difficulty Level</Label>
        <RadioGroup 
          value={difficulty} 
          onValueChange={(value) => setDifficulty(value as DifficultyLevel)}
          className="flex space-x-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="easy" id="easy" />
            <Label htmlFor="easy">Easy</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="medium" id="medium" />
            <Label htmlFor="medium">Medium</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="hard" id="hard" />
            <Label htmlFor="hard">Hard</Label>
          </div>
        </RadioGroup>
      </div>

      <div className="mb-4">
        <div className="flex items-center space-x-2 mb-2">
          <Checkbox 
            id="showPrompt" 
            checked={showPrompt}
            onCheckedChange={(checked) => setShowPrompt(checked === true)}
          />
          <label
            htmlFor="showPrompt"
            className="text-sm font-medium flex items-center"
          >
            <Terminal className="h-4 w-4 mr-1" /> 
            Customize OpenAI Prompt
          </label>
        </div>
        
        {showPrompt && (
          <div className="space-y-2 p-3 border rounded-md bg-muted/20">
            <div className="flex justify-between items-center">
              <Label className="text-sm font-medium">OpenAI Prompt Template</Label>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={handleResetPrompt}
              >
                Reset to Default
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mb-2">Select the type of prompt to use:</p>
            <RadioGroup 
              value={promptType}
              onValueChange={(value: "standard" | "topic" | "figure") => {
                setPromptType(value);
                console.log("Changed prompt type to:", value);
              }}
              className="flex space-x-4 mb-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="standard" id="standard" />
                <Label htmlFor="standard">Standard Questions</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="topic" id="topic" />
                <Label htmlFor="topic">Topic-Based Questions</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="figure" id="figure" />
                <Label htmlFor="figure" className="flex items-center">
                  <PenToolIcon className="h-4 w-4 mr-1" />
                  Questions with Figures/Canvas
                </Label>
              </div>
            </RadioGroup>
            
            <Textarea 
              value={aiPrompt} 
              onChange={(e) => setAiPrompt(e.target.value)}
              className="font-mono text-sm min-h-[200px]"
            />
            <p className="text-xs text-muted-foreground">
              Customize the prompt sent to OpenAI for generating questions. Variables like {"{difficulty}"}, {"{subject}"}, 
              and {"{category}"} will be automatically replaced with your selections.
            </p>
          </div>
        )}
      </div>
      
      <div className="flex justify-between items-center mb-4">
        {selectedSubject && (
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            {isLoadingCount ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Badge variant="outline" className="ml-2">
                {existingQuestionCount} existing question{existingQuestionCount !== 1 ? 's' : ''} in database
              </Badge>
            )}
          </div>
        )}
        
        <Button 
          onClick={handleSubmit} 
          disabled={!selectedCategory || isGenerating}
          className="ml-auto"
        >
          {isGenerating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isGenerating ? "Generating..." : `Generate ${
            promptType === "topic" ? "Topic-Based" : 
            promptType === "figure" ? "Figure-Based" : 
            "Standard"
          } Questions`}
        </Button>
      </div>
    </div>
  );
};

export default QuestionGeneratorForm;
